import React from "react";
import { Box, ListItemButton, Tooltip } from "@mui/material";
import { AllInclusive, Euro, EventBusy } from "@mui/icons-material";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import { getStatusColor } from "./RxForms/statusColor";
import { formatDateDisplay } from "../../../helpers/dateFormat";
import { getVisitTypeIcon } from "../../../helpers/visitTypeMapping";
import { getColorForRxNumber } from "../../../helpers/statusColor";
import { cardShell } from "../../../molecules/cardShell";
import { RxLightDto, TherapyRxSimpleDto } from "../../../api-client";
import { useMasterTables, isBlankoTarifGroup } from "../../../stores/MasterTables";

interface UnpaidBadgeData {
  dueAmount?: number;
  tarifGroup?: string;
}

interface RxListItemProps {
  rx: RxLightDto;
  isSelected?: boolean;
  onClick?: () => void;
  unpaidBadgeData?: UnpaidBadgeData;
  showRxNumber?: boolean;
  /**
   * How this item sits in its surroundings.
   *
   * - `card` (default): a standalone summary — carries its own `cardShell`
   *   (white, hairline, radius, shadow). Used above an Rx's appointment list.
   * - `row`: one line of a selectable list. Flush and shell-less — the LIST
   *   owns the border, radius and separators, the row owns only its Rx rail,
   *   a hover tint and a selection tint. Rows-as-floating-cards is the
   *   "homebrew" look; see "Dense lists: bordered rows, not rounded-rect
   *   cards" in the design system.
   */
  variant?: "card" | "row";
}

/** Same tokens as `TherapySlotRow`, so the two read as one system and both
 *  respond to a container's `data-density`. */
const ICON_SLOT = "var(--row-slot)";

const denseIcons = { "& .MuiSvgIcon-root": { fontSize: "var(--row-icon)" } };

const visitLabel = (isHomeVisit?: boolean | null): string =>
  isHomeVisit === true ? "Haus/Heim" : isHomeVisit === false ? "Praxis" : "Kein Ort";

/**
 * Summary of a prescription: date, place of treatment, its therapies with
 * prescribed amounts, processing status, and whether money is outstanding.
 *
 * Every one of those used to be a `Chip` — seven drawn boxes in a row, on a
 * grey `Paper`, with the perpetual marker as a 40px "∞" glyph. They are plain
 * text and icons now, on the shared `cardShell` (design-system rule 1: the
 * container carries the edge, the values do not each get their own).
 *
 * Colour is kept only where it is data (rule 4): the processing status as a
 * dot, the outstanding-payment symbol, and the 4px rail that ties this card to
 * the Rx colour used by the appointment rows beneath it. Therapy colour is
 * deliberately dropped here as it was in `TherapySlotRow`.
 */
export const RxListItem: React.FC<RxListItemProps> = ({
  rx,
  isSelected = false,
  onClick,
  unpaidBadgeData,
  showRxNumber = true,
  variant = "card",
}) => {
  const therapies = rx.therapyRxs
    ?.slice()
    .sort((a: TherapyRxSimpleDto, b: TherapyRxSimpleDto) =>
      (a.therapyAbbr ?? "").localeCompare(b.therapyAbbr ?? "")
    );

  const isRow = variant === "row";

  // Derived from the catalog, not a hardcoded ["Blanko", "PostBlanko"] pair:
  // the set grows with ErgoBlanko without a change here.
  const { data: masterTablesData } = useMasterTables();
  const isBlanko = isBlankoTarifGroup(masterTablesData, rx.tarifGroupId);

  const content = (
    <Box
      className={
        isRow
          ? [
              "flex w-full min-w-0 items-center transition-colors duration-fast",
              // Selection is a tint of --interaction (the "you are here"
              // token), not a lifted shadow — a flush row has nothing to lift.
              isSelected ? "bg-interaction/10" : "hover:bg-ink-band",
            ].join(" ")
          : cardShell({ isSelected })
      }
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        minWidth: 0,
        // Rows get a slightly taller box: they are click targets in a list,
        // not summaries competing with the content below them.
        padding: isRow ? "6px 8px" : "3px 6px",
        // Card only: the Rx's own colour, as the 4px accent `.mc-event` uses —
        // the same value the appointment rows below the summary put on their
        // rails, so the two read as one group. List rows get NO rail: in the
        // selector every row is a different Rx, so a per-row colour would
        // encode nothing, and the rail's real job (grouping slots by Rx) has
        // no counterpart here.
        borderLeft: isRow
          ? undefined
          : `4px solid ${
              rx.rxNumber ? getColorForRxNumber(rx.rxNumber) : mobiliTheme.mainHeader
            }`,
        // Deleted prescriptions keep their existing muted treatment, but as a
        // tint of the card rather than a different fill.
        opacity: rx.deletedAt ? 0.6 : 1,
      }}
    >
      {showRxNumber && rx.rxNumber && (
        // Muted against the date: the number identifies the Rx but the date is
        // what a therapist scans this list for, so the date carries the ink.
        <span className="shrink-0 whitespace-nowrap text-lead font-semibold tabular-nums text-ink-muted">
          {rx.rxNumber}
        </span>
      )}

      {rx.therapyType === "Ergotherapie" && (
        <Tooltip title="Ergotherapie">
          <span className="shrink-0 text-mini font-bold uppercase" style={{ color: "#6a1b9a" }}>
            Ergo
          </span>
        </Tooltip>
      )}

      {rx.date ? (
        // Same size and weight as the Rx number beside it: both are
        // identifiers for the card, and they head the row together. The date
        // takes the full-strength ink of the pair.
        <span className="shrink-0 whitespace-nowrap text-lead font-semibold tabular-nums text-ink-text">
          {formatDateDisplay(rx.date)}
        </span>
      ) : (
        <Tooltip title="Kein Datum">
          <Box sx={{ ...denseIcons, flexShrink: 0, display: "flex" }}>
            <EventBusy sx={{ color: mobiliTheme.palette.text.disabled }} />
          </Box>
        </Tooltip>
      )}

      <Tooltip title={visitLabel(rx.isHomeVisit)}>
        <Box
          sx={{
            ...denseIcons,
            flexShrink: 0,
            width: ICON_SLOT,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {getVisitTypeIcon(rx.isHomeVisit === true ? "haus" : "praxis")}
        </Box>
      </Tooltip>

      {/* Therapies grow into whatever is left, and clip rather than ellipsise —
          at this width the dots would cost more characters than they explain. */}
      {isBlanko ? (
        // A Blankoverordnung's lines (PD, BD, the therapist's plan …) are an
        // implementation detail of the tarif, not what identifies the Rx —
        // in this dense selector row the tarif name says it all. The concrete
        // lines stay one hover away and remain fully visible in the Rx view's
        // therapy table.
        <Tooltip
          title={
            therapies
              ?.map((t: TherapyRxSimpleDto) =>
                `${t.amount ? `${t.amount} ` : ""}${t.therapyAbbr ?? ""}`.trim()
              )
              .filter(Boolean)
              .join(", ") || ""
          }
        >
          <span className="flex min-w-0 flex-1 gap-2 overflow-hidden whitespace-nowrap text-title font-semibold text-ink-text">
            {rx.tarifGroupId}
          </span>
        </Tooltip>
      ) : (
        <span className="flex min-w-0 flex-1 gap-2 overflow-hidden whitespace-nowrap text-title font-semibold text-ink-text">
          {therapies?.map((therapyRx: TherapyRxSimpleDto, index: number) => (
            <span key={index}>
              {therapyRx.amount ? `${therapyRx.amount} ` : ""}
              {therapyRx.therapyAbbr}
            </span>
          ))}
        </span>
      )}

      <Box
        sx={{
          ...denseIcons,
          flexShrink: 0,
          width: ICON_SLOT,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          // Stretches to the row's height so the status capsule below can fill
          // it; the row itself still measures by its text.
          alignSelf: "stretch",
        }}
      >
        {rx.isPerpetual ? (
          <Tooltip title="Dauerrezept">
            <AllInclusive />
          </Tooltip>
        ) : (
          <Tooltip title={`${rx?.processingStatus ?? "Kein Status"}`}>
            {/* A full-height capsule rather than a dot: the row's height is
                already paid for, and an 8px dot floating in it read as an
                afterthought. `self-stretch` beats the parent's `center`.

                getStatusColor has no default case and returns undefined for an
                unset or unrecognised status. The Avatar this replaced fell back
                to MUI's own grey and stayed visible; an unstyled mark would just
                vanish. `bg-ink-muted` is the floor and the inline colour paints
                over it whenever there is a real status to show. */}
            <span
              className="w-1.5 shrink-0 self-stretch rounded-pill bg-ink-muted"
              style={{ backgroundColor: getStatusColor(rx?.processingStatus) }}
            />
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          ...denseIcons,
          flexShrink: 0,
          width: ICON_SLOT,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {unpaidBadgeData && (
          <Tooltip
            title={`Offener Betrag: ${unpaidBadgeData.dueAmount ?? "-"}${
              unpaidBadgeData.tarifGroup === "GKV" ? " (Zuzahlung)" : ""
            }`}
          >
            <Euro
              sx={{
                color:
                  unpaidBadgeData.tarifGroup === "GKV"
                    ? mobiliTheme.palette.error.main
                    : mobiliTheme.palette.info.main,
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  // If onClick is provided, wrap in ListItemButton for interactivity
  if (onClick) {
    return (
      <ListItemButton
        onClick={onClick}
        selected={isSelected}
        // The content carries its own hover/selection tints (ink-band /
        // interaction), so MUI's overlays are silenced: its defaults are
        // hue-neutral black washes (rule 1), and they would stack on ours.
        sx={{
          padding: 0,
          borderRadius: isRow ? 0 : "var(--radius-card)",
          "&.Mui-selected": { background: "transparent" },
          "&:hover, &.Mui-selected:hover": { background: "transparent" },
        }}
      >
        {content}
      </ListItemButton>
    );
  }

  // Otherwise, return the content directly
  return content;
};
